const normalizeText = (value) => String(value || '').trim();

const toArray = (value) => (Array.isArray(value) ? value : []);

const toTitleLabel = (value = '') => normalizeText(value)
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .replace(/\b\w/g, (match) => match.toUpperCase());

const normalizeStoryBucket = (bucket = {}) => {
  if (!bucket || typeof bucket !== 'object') {
    return null;
  }

  const bucketType = normalizeText(bucket.bucketType || bucket.type);
  const label = normalizeText(bucket.label) || toTitleLabel(bucketType || 'Detail');
  const summary = normalizeText(bucket.summary || bucket.text || bucket.notes || bucket.sourceText);
  const sourceText = normalizeText(bucket.sourceText);

  if (!summary && !sourceText) {
    return null;
  }

  return {
    bucketType,
    label,
    summary: summary || sourceText,
    sourceText,
    role: normalizeText(bucket.role),
    certainty: normalizeText(bucket.certainty),
  };
};

const normalizeStoryBuckets = (metadata = {}) => (
  toArray(metadata.storyBuckets)
    .map(normalizeStoryBucket)
    .filter(Boolean)
);

const normalizeStringArray = (value) => (
  toArray(value)
    .map((item) => normalizeText(item))
    .filter(Boolean)
);

const normalizeSourceAttachment = (attachment = {}) => {
  if (!attachment || typeof attachment !== 'object') return null;
  const url = normalizeText(attachment.url || attachment.downloadUrl || attachment.href);
  if (!url) return null;

  return {
    url,
    fileName: normalizeText(attachment.fileName || attachment.filename || attachment.name),
    type: normalizeText(attachment.type || attachment.mimeType || attachment.contentType || 'image'),
    path: normalizeText(attachment.path || attachment.storagePath),
  };
};

const normalizeSourceAttachments = (entry = {}, entryConfig = {}, metadata = {}, source = {}) => {
  const attachments = [
    ...toArray(metadata.source?.imageAttachments),
    ...toArray(metadata.source?.audioAttachments),
    ...toArray(metadata.sourceImageAttachments),
    ...toArray(source.imageAttachments),
    ...toArray(source.audioAttachments),
    ...toArray(entry.imageAttachments),
    ...toArray(entry.audioAttachments),
    ...toArray(entryConfig.imageAttachments),
    ...toArray(entryConfig.audioAttachments),
  ]
    .map(normalizeSourceAttachment)
    .filter(Boolean);

  if (attachments.length) return attachments;

  return [
    ...toArray(metadata.source?.mediaUrls),
    ...toArray(source.mediaUrls),
    ...toArray(entry.sourceMediaUrls),
  ]
    .map((item) => (
      typeof item === 'string'
        ? normalizeSourceAttachment({ url: item, type: 'image' })
        : normalizeSourceAttachment(item)
    ))
    .filter(Boolean);
};

const getMetadata = (entry = {}, entryConfig = {}) => ({
  ...(entry?.metadata && typeof entry.metadata === 'object' ? entry.metadata : {}),
  ...(entryConfig?.metadata && typeof entryConfig.metadata === 'object' ? entryConfig.metadata : {}),
});

const getSource = (entry = {}, entryConfig = {}) => ({
  ...(entry?.source && typeof entry.source === 'object' ? entry.source : {}),
  ...(entryConfig?.source && typeof entryConfig.source === 'object' ? entryConfig.source : {}),
});

const isCaptureFirstEntry = (entry = {}, entryConfig = {}) => {
  const metadata = getMetadata(entry, entryConfig);
  const source = getSource(entry, entryConfig);

  return Boolean(
    metadata.captureFirst
    || metadata.sourceMode === 'quick_log_capture_first'
    || metadata.inputMode === 'capture_first'
    || entry?.sourceMode === 'quick_log_capture_first'
    || entry?.inputMode === 'capture_first'
    || source.type === 'quick_log_capture_first'
    || source.mode === 'capture_first'
  );
};

const getOriginalNoteText = (entry = {}, entryConfig = {}, metadata = {}) => normalizeText(
  metadata.originalRawText
  || metadata.rawSourceText
  || entry.rawSourceText
  || entry.rawText
  || entry.originalText
  || entryConfig.originalText
  || entry.sourceText
  || entryConfig.sourceText
  || ''
);

const normalizeReviewDestinations = (...values) => {
  const counts = {};
  values.forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((label) => {
        const normalized = normalizeText(label);
        if (normalized) counts[normalized] = (counts[normalized] || 0) + 1;
      });
      return;
    }
    if (!value || typeof value !== 'object') return;
    Object.entries(value).forEach(([label, count]) => {
      const normalized = normalizeText(label);
      const numericCount = Number(count);
      if (normalized && numericCount > 0) {
        counts[normalized] = (counts[normalized] || 0) + numericCount;
      }
    });
  });
  return counts;
};

export const getCaptureFirstTimelineContext = (entry = {}, entryConfig = {}) => {
  if (!isCaptureFirstEntry(entry, entryConfig)) {
    return null;
  }

  const metadata = getMetadata(entry, entryConfig);
  const source = getSource(entry, entryConfig);
  const captureFirstMetadata = metadata.captureFirst && typeof metadata.captureFirst === 'object'
    ? metadata.captureFirst
    : {};
  const hidePickedUpDetails = Boolean(metadata.hidePickedUpDetails);
  const storyBuckets = hidePickedUpDetails ? [] : normalizeStoryBuckets(metadata);
  const rawHints = normalizeStringArray(metadata.rawHints);
  const reviewReasons = normalizeStringArray(
    metadata.reviewReasons
    || captureFirstMetadata.reviewReasons
    || entry.reviewConfidenceReasons
  );
  const reviewDestinations = normalizeReviewDestinations(
    metadata.pendingReviewDestinations,
    metadata.reviewDestinations,
    metadata.structuredReviewDestinations,
    captureFirstMetadata.pendingReviewDestinations,
    captureFirstMetadata.reviewDestinations,
    captureFirstMetadata.structuredReviewDestinations,
    entry.pendingReviewDestinations,
    entry.reviewDestinations
  );
  const originalNote = getOriginalNoteText(entry, entryConfig, metadata);
  const sourceAttachments = normalizeSourceAttachments(entry, entryConfig, metadata, source);
  const needsReview = Boolean(
    metadata.needsReview
    || metadata.captureFirstReviewState === 'needs_review'
    || entry.reviewConfidence === 'review'
  );
  const isRefreshing = Boolean(
    entry.refreshState === 'refreshing'
    || metadata.timelineEditRefresh?.status === 'refreshing'
    || metadata.captureFirst?.status === 'refreshing'
  );

  return {
    needsReview,
    isRefreshing,
    reviewReasons,
    reviewDestinations,
    storyBuckets,
    rawHints,
    originalNote,
    sourceAttachments,
    sourceNoteId: metadata.sourceNoteId || entry.sourceNoteId || null,
    sourceNotePath: metadata.sourceNotePath || entry.sourceNotePath || '',
    hasDetails: !hidePickedUpDetails && (isRefreshing || storyBuckets.length > 0 || rawHints.length > 0 || reviewReasons.length > 0),
    hasOriginalNote: Boolean(originalNote),
    hasSourceAttachments: sourceAttachments.length > 0,
    hasVisibleContent: isRefreshing || needsReview || storyBuckets.length > 0 || rawHints.length > 0 || reviewReasons.length > 0 || Boolean(originalNote) || sourceAttachments.length > 0,
  };
};

export default getCaptureFirstTimelineContext;
